import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, Loader2, Settings, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { BACKEND_URL } from '../../config';

const BrowserProviderSection = ({ onProviderSwitched }) => {
  const [browserProvider, setBrowserProvider] = useState('browsercat');
  const [availableProviders, setAvailableProviders] = useState({});
  const [loadingBrowserProvider, setLoadingBrowserProvider] = useState(false);
  const [switchingProvider, setSwitchingProvider] = useState(false);

  const fetchBrowserProvider = async () => {
    setLoadingBrowserProvider(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/browser-provider`);
      const data = await response.json();
      if (data.status === 'success') {
        setBrowserProvider(data.provider || 'browsercat');
        setAvailableProviders(data.available_providers || {});
      }
    } catch (error) {
      console.error('Error fetching browser provider:', error);
      toast.error('Failed to load browser provider config');
    } finally {
      setLoadingBrowserProvider(false);
    }
  };

  const handleSwitchBrowserProvider = async (newProvider) => {
    setSwitchingProvider(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/browser-provider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: newProvider }),
      });
      const data = await response.json();
      if (response.ok) {
        setBrowserProvider(newProvider);
        toast.success(data.message || `Switched to ${newProvider}`);
        if (typeof onProviderSwitched === 'function') onProviderSwitched();
      } else {
        toast.error(data.detail || 'Failed to switch browser provider');
      }
    } catch (error) {
      console.error('Error switching browser provider:', error);
      toast.error('Failed to switch browser provider');
    } finally {
      setSwitchingProvider(false);
    }
  };

  useEffect(() => {
    fetchBrowserProvider();
  }, []);

  return (
    <div className="space-y-6">
      <Card className="shadow-lg border-2 border-purple-200">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b-2 border-purple-200">
          <CardTitle className="flex items-center gap-2 text-xl text-slate-800">
            <Settings className="w-6 h-6 text-purple-600" />
            Browser Provider Configuration
          </CardTitle>
          <CardDescription>
            Choose which cloud browser service to use for scraping. Switch between providers if one is experiencing issues.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {loadingBrowserProvider ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-500" />
              <p className="mt-2 text-slate-600">Loading configuration...</p>
            </div>
          ) : (
            <>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h3 className="font-semibold text-slate-800 mb-3">Current Provider</h3>
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${browserProvider === 'browsercat' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                  <span className="text-lg font-medium">
                    {browserProvider === 'browsercat' ? 'BrowserCat' : 'Browserless'}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                    browserProvider === 'browsercat'
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-200 hover:border-green-300 bg-white'
                  }`}
                  onClick={() => browserProvider !== 'browsercat' && handleSwitchBrowserProvider('browsercat')}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-lg text-slate-800">🐱 BrowserCat</h4>
                    {browserProvider === 'browsercat' && (
                      <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">Active</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-3">
                    Playwright-native cloud browser service with simple WebSocket connection.
                  </p>
                  <div className="flex items-center gap-2 text-xs">
                    {availableProviders.browsercat?.configured ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> API Key Configured
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> Not Configured
                      </span>
                    )}
                  </div>
                  {switchingProvider && browserProvider !== 'browsercat' && (
                    <div className="mt-2">
                      <Loader2 className="w-4 h-4 animate-spin text-green-500" />
                    </div>
                  )}
                </div>

                <div
                  className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                    browserProvider === 'browserless'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-blue-300 bg-white'
                  }`}
                  onClick={() => browserProvider !== 'browserless' && handleSwitchBrowserProvider('browserless')}
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-lg text-slate-800">🌐 Browserless</h4>
                    {browserProvider === 'browserless' && (
                      <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">Active</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-3">
                    Enterprise-grade headless browser service with CDP connection.
                  </p>
                  <div className="flex items-center gap-2 text-xs">
                    {availableProviders.browserless?.configured ? (
                      <span className="text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> API Key Configured
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> Not Configured
                      </span>
                    )}
                  </div>
                  {switchingProvider && browserProvider !== 'browserless' && (
                    <div className="mt-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  How It Works
                </h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Click on a provider card to switch to that service</li>
                  <li>The change takes effect immediately for new scraping jobs</li>
                  <li>Ongoing scraping jobs will continue using the previous provider</li>
                  <li>Switch providers if one service is experiencing downtime or issues</li>
                </ul>
              </div>

              {browserProvider === 'browserless' && (
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Browserless Concurrency Note
                  </h4>
                  <p className="text-sm text-amber-800">
                    Your Browserless plan has a <strong>concurrency limit of 1</strong>. This means only one browser session can run at a time. The scraper handles this automatically by processing PMs sequentially. Avoid starting multiple scrape jobs simultaneously.
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BrowserProviderSection;
